import { useCallback, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { searchOntologyTerms, type TermCard } from "../../../api/content/ontologyApi";
import { useLoad } from "../../../shared/loading/useLoad";
import DropdownPopover from "../../../ui/overlays/DropdownPopover";
import { FORM_COMPACT_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import { orderedLabels } from "../viewer/ontologyModel";
import { compactIri } from "../viewer/types";
import Chip from "../../../ui/feedback/Chip";
import Field from "../../../ui/forms/Field";
import LoadStatus from "../../../ui/feedback/LoadStatus";

const SEARCH_DELAY_MS = 250;
const NO_RESULTS: readonly TermCard[] = [];

interface TermSearchProps {
  projectId: string;
  commit: string;
  onPick: (term: TermCard) => void;
}

/** Find a term of the version by label or IRI; picking one opens it. */
export default function TermSearch({ projectId, commit, onPick }: TermSearchProps) {
  const { t, i18n } = useTranslation();
  const listId = useId();
  const [text, setText] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(text.trim());
    }, SEARCH_DELAY_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [text]);

  const load = useCallback(
    (signal: AbortSignal) => (query === "" ? Promise.resolve(NO_RESULTS) : searchOntologyTerms(projectId, commit, query, signal)),
    [projectId, commit, query],
  );
  const { state, reload } = useLoad(load);
  const clear = () => {
    setText("");
    setQuery("");
  };

  return (
    <div className="app-dropdown-anchor w-full max-w-xl">
      <Field label={t("ontologyViewer.search.label")}>
        {(control) => (
          <input
            {...control}
            type="search"
            className={`${FORM_COMPACT_CONTROL_CLASS} w-full`}
            value={text}
            placeholder={t("ontologyViewer.search.placeholder")}
            aria-controls={listId}
            onChange={(event) => {
              setText(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                clear();
              }
            }}
          />
        )}
      </Field>
      <DropdownPopover open={query !== ""} align="right" width="full">
        <div id={listId} className="max-h-80 overflow-y-auto p-1">
          <LoadStatus state={state} loadingLabel={t("ontologyViewer.search.searching")} onRetry={reload} compact />
          {state.status === "loaded" && state.value.length === 0 ? (
            <p className="app-text-muted px-2 py-1.5 text-sm">{t("ontologyViewer.search.none")}</p>
          ) : null}
          {state.status === "loaded" ? (
            <ul>
              {state.value.map((term) => (
                <li key={term.iri}>
                  <button
                    type="button"
                    className="app-action-link flex w-full flex-col rounded px-2 py-1.5 text-left text-sm hover:opacity-80"
                    title={term.iri}
                    onClick={() => {
                      onPick(term);
                      clear();
                    }}
                  >
                    <span className="flex items-center gap-2">
                      <span className="truncate font-medium">{orderedLabels(term.labels, i18n.language)[0]?.value ?? compactIri(term.iri)}</span>
                      <Chip size="compact">{t(`ontologyViewer.termKinds.${term.kind}`)}</Chip>
                    </span>
                    <span className="app-text-muted truncate font-mono text-xs">{term.iri}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </DropdownPopover>
    </div>
  );
}
